import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import {
  trackContentCardBookmark,
  trackContentCardUnbookmark,
} from '../../../models/content/analytics';
import { contentOperations, contentSelectors } from '../../../models/content';
import { useDispatch } from 'react-redux';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { useQueryClient } from '@tanstack/react-query';
import { updateFeedContentCache } from '../../../common/queries/update-feed-content-cache';
import {
  Button,
  Icon,
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';

interface IContentActionBookmark {
  isBookmarked: boolean;
  onClick: MouseEventHandler | undefined;
  contentTitle: string;
  showText?: boolean;
  wideMode?: boolean;
  container?: HTMLElement | null;
}

const ContentActionBookmark: React.FC<IContentActionBookmark> = ({
  isBookmarked,
  onClick,
  contentTitle,
  showText = false,
  wideMode = false,
  container,
}) => {
  //see comment in content-action-share.tsx
  const className = cx('solar-shim', {
    'content__action--bookmarked': isBookmarked,
  });

  const { t } = useTranslation(undefined, { useSuspense: false });
  const bookmarkKey = isBookmarked
    ? 'content.bookmark_remove'
    : 'content.bookmark';
  const bookmarkActionKey = isBookmarked
    ? 'content.bookmark_remove_action'
    : 'content.bookmark_action';

  const displayText = showText || wideMode;

  const actionLabel = t(bookmarkActionKey, { title: contentTitle });

  return displayText ? (
    <Button
      className={className}
      variant="text"
      onClick={onClick}
      aria-label={actionLabel}
      label={t(bookmarkKey)}
      selected={isBookmarked}
      leftIcon={<Icon>bookmark</Icon>}
    />
  ) : (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <IconButton
          className={className}
          aria-label={actionLabel}
          iconFilled={isBookmarked}
          iconName="bookmark"
          onClick={onClick}
        />
      </TooltipTrigger>
      <TooltipContent description={t(bookmarkKey)} container={container} />
    </TooltipRoot>
  );
};

const ContentActionBookmarkWrapper = ({
  contentId,
  showText,
  analyticsData,
  wideMode,
  container,
}: {
  showText?: boolean;
  wideMode?: boolean;
  contentId: number | string;
  analyticsData: IAnalyticsData;
  container?: HTMLElement | null;
}) => {
  const isBookmarked = usePatronSelector((s) =>
    contentSelectors.getContentBookmarked(s, { contentId: Number(contentId) })
  );
  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, {
      contentId: Number(contentId),
    })
  );
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const bookmarkContent = (id: string | number) =>
    dispatch(contentOperations.bookmarkContent(id));
  const unbookmarkContent = (id: string | number) =>
    dispatch(contentOperations.unbookmarkContent(id));

  const handleClick = async () => {
    const bookmarked = !isBookmarked;
    try {
      if (isBookmarked) {
        trackContentCardUnbookmark(contentId, analyticsData);
        await unbookmarkContent(contentId);
      } else {
        trackContentCardBookmark(contentId, analyticsData);
        await bookmarkContent(contentId);
      }
      // Keep localStorage React Query cache in sync so state persists across page reloads
      updateFeedContentCache(queryClient, Number(contentId), { bookmarked });
    } catch {
      // thunk already rolled back Redux; cache was not updated so no rollback needed
    }
  };

  return (
    <ContentActionBookmark
      isBookmarked={isBookmarked}
      showText={showText}
      onClick={handleClick}
      contentTitle={contentTitle}
      wideMode={wideMode}
      container={container}
    />
  );
};

export { ContentActionBookmark };

export default ContentActionBookmarkWrapper;
